/**
 * Quick Shop — modal (desktop) / drawer (mobile) for picking variants from the PLP.
 * Fetches /products/{handle}?view=quick-shop, injects content, wires up variant + add-to-cart.
 */

class QuickShopDialog extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.quick-shop-dialog__panel');
    this.content = this.querySelector('[data-quick-shop-content]');
    this.closeBtns = this.querySelectorAll('[data-quick-shop-close]');

    this.closeBtns.forEach((btn) => btn.addEventListener('click', () => this.close()));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    window.QuickShop = this;
  }

  async open(handle) {
    if (!handle) return;
    this.content.innerHTML = '<div class="quick-shop-dialog__loading">Loading…</div>';
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('quick-shop-open');
    this.isOpen = true;

    try {
      const res = await fetch(`/products/${handle}?view=quick-shop`);
      if (!res.ok) throw new Error('Fetch failed');
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const form = doc.querySelector('quick-shop-form');
      if (!form) throw new Error('No quick-shop-form in response');
      this.content.innerHTML = '';
      this.content.appendChild(form);
      this.initForm(form);
    } catch (err) {
      this.content.innerHTML = `<div class="quick-shop-dialog__loading">Could not load product.</div>`;
      console.error('[quick-shop]', err);
    }
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('quick-shop-open');
    this.isOpen = false;
    // Delay clear so fade-out can play
    setTimeout(() => {
      if (!this.isOpen) this.content.innerHTML = '';
    }, 300);
  }

  initForm(form) {
    const variantData = form.querySelector('[data-quick-shop-variants]');
    const variants = variantData ? JSON.parse(variantData.textContent) : [];
    const priceEl = form.querySelector('[data-quick-shop-price]');
    const variantIdInput = form.querySelector('[data-quick-shop-variant-id]');
    const addBtn = form.querySelector('[data-quick-shop-add]');
    const addLabel = form.querySelector('[data-quick-shop-add-label]');
    const formEl = form.querySelector('[data-quick-shop-form]');

    // Current option selection — read from aria-pressed state
    const optionCount = form.querySelectorAll('.quick-shop__option').length;
    const selected = [];
    for (let i = 1; i <= optionCount; i++) {
      const btn = form.querySelector(`.quick-shop__option-value.is-selected[data-option-position="${i}"]`);
      selected[i - 1] = btn ? btn.dataset.optionValue : null;
    }

    const findVariant = () => {
      return variants.find((v) =>
        selected.every((val, idx) => v.options[idx] === val)
      );
    };

    const updateForVariant = (variant) => {
      if (!variant) {
        addBtn.disabled = true;
        addLabel.textContent = 'Unavailable';
        return;
      }
      variantIdInput.value = variant.id;

      // Price
      if (priceEl) {
        const money = (cents) => {
          // Use Shopify's money format if available via theme settings; fallback to default
          const amt = (cents / 100).toFixed(2);
          return window.Shopify?.formatMoney
            ? window.Shopify.formatMoney(cents)
            : `$${amt}`;
        };
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceEl.innerHTML = `<s class="quick-shop__price-compare">${money(variant.compare_at_price)}</s>${money(variant.price)}`;
        } else {
          priceEl.textContent = money(variant.price);
        }
      }

      // Add-to-cart state
      if (variant.available) {
        addBtn.disabled = false;
        addLabel.textContent = 'Add to cart';
      } else {
        addBtn.disabled = true;
        addLabel.textContent = 'Sold out';
      }
    };

    const updateAvailability = () => {
      // For each option value button, mark unavailable if no in-stock variant matches the rest of the current selection
      form.querySelectorAll('.quick-shop__option-value').forEach((btn) => {
        const pos = parseInt(btn.dataset.optionPosition, 10) - 1;
        const val = btn.dataset.optionValue;
        const trial = selected.slice();
        trial[pos] = val;
        const match = variants.find((v) =>
          trial.every((tv, idx) => tv == null || v.options[idx] === tv)
        );
        btn.classList.toggle('is-unavailable', !match || !match.available);
      });
    };

    // Wire up option buttons
    form.querySelectorAll('.quick-shop__option-value').forEach((btn) => {
      btn.addEventListener('click', () => {
        const pos = parseInt(btn.dataset.optionPosition, 10) - 1;
        selected[pos] = btn.dataset.optionValue;

        // Toggle selected state on siblings
        form
          .querySelectorAll(`.quick-shop__option-value[data-option-position="${pos + 1}"]`)
          .forEach((b) => {
            b.classList.toggle('is-selected', b === btn);
            b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
          });

        // Update selected-value readout in the option label
        const readout = form.querySelector(`[data-selected-for="${pos + 1}"]`);
        if (readout) readout.textContent = btn.dataset.optionValue;

        const variant = findVariant();
        updateForVariant(variant);
        updateAvailability();
      });
    });

    // Initial availability pass
    updateAvailability();

    // Form submit → /cart/add.js → reload cart drawer
    formEl.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (addBtn.disabled) return;
      addBtn.classList.add('is-loading');
      addLabel.textContent = 'Adding…';

      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const sections = cartDrawer
          ? cartDrawer.getSectionsToRender?.().map((s) => s.id).join(',')
          : '';

        const formData = new FormData(formEl);
        if (sections) formData.append('sections', sections);
        formData.append('sections_url', window.location.pathname);

        const res = await fetch(window.Shopify?.routes?.root
          ? `${window.Shopify.routes.root}cart/add.js`
          : '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
          body: formData,
        });
        const data = await res.json();

        if (data.status) {
          addBtn.classList.remove('is-loading');
          addLabel.textContent = data.description || 'Error';
          return;
        }

        // Refresh cart drawer
        if (cartDrawer && data.sections) {
          cartDrawer.renderContents({ ...data, items: [data] });
        } else if (cartDrawer) {
          cartDrawer.open?.();
        }

        // Close quick shop and open cart drawer
        this.close();
        setTimeout(() => cartDrawer?.open?.(), 250);
      } catch (err) {
        console.error('[quick-shop add]', err);
        addBtn.classList.remove('is-loading');
        addLabel.textContent = 'Try again';
      }
    });
  }
}

if (!customElements.get('quick-shop-dialog')) {
  customElements.define('quick-shop-dialog', QuickShopDialog);
}

// Empty custom element for the content wrapper (keeps the DOM tidy)
if (!customElements.get('quick-shop-form')) {
  customElements.define('quick-shop-form', class extends HTMLElement {});
}

// Global click delegate — any [data-quick-shop-trigger] opens the dialog.
// Capture phase + elementsFromPoint fallback so Dawn's full-card anchor can't
// win the hit-test and navigate to the PDP first.
const openFromTrigger = (trigger) => {
  if (!trigger) return;
  const handle = trigger.dataset.quickShopHandle;
  window.QuickShop?.open(handle);
};

document.addEventListener(
  'click',
  (e) => {
    let trigger = e.target.closest?.('[data-quick-shop-trigger]');
    if (!trigger) {
      // e.target might be Dawn's overlay anchor sitting on top — check element stack
      const stack = document.elementsFromPoint
        ? document.elementsFromPoint(e.clientX, e.clientY)
        : [];
      for (const el of stack) {
        const t = el.closest?.('[data-quick-shop-trigger]');
        if (t) { trigger = t; break; }
      }
    }
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    openFromTrigger(trigger);
  },
  true
);

// Also block pointerdown on the trigger so Dawn doesn't start a nav
document.addEventListener(
  'pointerdown',
  (e) => {
    const stack = document.elementsFromPoint
      ? document.elementsFromPoint(e.clientX, e.clientY)
      : [e.target];
    for (const el of stack) {
      if (el.closest?.('[data-quick-shop-trigger]')) {
        e.stopPropagation();
        return;
      }
    }
  },
  true
);

// Lock body scroll when dialog is open
const style = document.createElement('style');
style.textContent = `body.quick-shop-open { overflow: hidden; }`;
document.head.appendChild(style);
